import { useSortable, defaultAnimateLayoutChanges, type AnimateLayoutChanges } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { formatDate, isPastDateOnly } from '@/lib/date';
import type { TaskItem, TaskType } from '@hygge/shared';
import { CheckSquare, Clock, MessageSquare } from 'lucide-react';
import { memo, useRef } from 'react';
import { AssigneeStack } from './assignee-stack';
import { PriorityBars, PRIORITY_CONFIG } from './task-config';

interface TaskCardProps {
  task: TaskItem;
  isOverlay?: boolean;
  onSelect?: (key: string) => void;
  /** Bulk select-mode: renders a checkbox and routes clicks to onToggleSelect. */
  selectionMode?: boolean;
  selected?: boolean;
  onToggleSelect?: (key: string) => void;
}

const typeKeyColors: Record<TaskType, string> = {
  task: '#3b82f6',
  bug: '#ef4444',
  story: '#8b5cf6',
  epic: '#f59e0b',
};

const animateLayoutChanges: AnimateLayoutChanges = (args) =>
  defaultAnimateLayoutChanges({ ...args, wasDragging: true });

export const TaskCard = memo(function TaskCard({
  task,
  isOverlay,
  onSelect,
  selectionMode,
  selected,
  onToggleSelect,
}: TaskCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: task.id,
    // Drag is disabled while selecting so ticking cards never starts a reorder.
    disabled: isOverlay || selectionMode,
    animateLayoutChanges,
  });

  const didDragRef = useRef(false);
  const pointerStartRef = useRef<{ x: number; y: number } | null>(null);

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : undefined,
  };

  const priorityCfg = PRIORITY_CONFIG[task.priority];
  const typeColor = typeKeyColors[task.type];

  const mergedListeners = isOverlay || selectionMode
    ? {}
    : {
        ...listeners,
        onPointerDown: (e: React.PointerEvent) => {
          pointerStartRef.current = { x: e.clientX, y: e.clientY };
          didDragRef.current = false;
          listeners?.onPointerDown?.(e as never);
        },
        onPointerMove: (e: React.PointerEvent) => {
          if (pointerStartRef.current) {
            const dx = e.clientX - pointerStartRef.current.x;
            const dy = e.clientY - pointerStartRef.current.y;
            if (Math.abs(dx) > 4 || Math.abs(dy) > 4) {
              didDragRef.current = true;
            }
          }
        },
      };

  const handleClick = (e: React.MouseEvent) => {
    if (isOverlay || didDragRef.current) return;
    if (selectionMode) {
      e.stopPropagation();
      onToggleSelect?.(task.key);
      return;
    }
    if (!onSelect) return;
    e.stopPropagation();
    onSelect(task.key);
  };

  const typeLabel = task.type.charAt(0).toUpperCase() + task.type.slice(1);
  const subtaskCount = task._count?.children ?? 0;
  const commentCount = task._count?.comments ?? 0;

  return (
    <TooltipProvider delayDuration={300}>
      <div
        ref={setNodeRef}
        style={isOverlay ? undefined : style}
        {...(isOverlay ? {} : attributes)}
        {...mergedListeners}
        onClick={isOverlay ? undefined : handleClick}
        className={cn(
          'rounded-lg border border-border bg-card p-2.5 shadow-sm select-none text-[11px]',
          !isOverlay && 'cursor-pointer',
          !isDragging && !isOverlay && 'hover:shadow-md',
          isOverlay && 'shadow-xl ring-2 ring-primary/20',
          selectionMode && 'relative',
          selected && 'ring-2 ring-hygge',
        )}
        {...(!isOverlay && !isDragging && { 'data-slot': 'task-card' })}
      >
        {selectionMode && (
          <div
            className="absolute left-1.5 top-1.5 z-10"
            onClick={(e) => e.stopPropagation()}
          >
            <Checkbox
              checked={!!selected}
              onCheckedChange={() => onToggleSelect?.(task.key)}
              aria-label={`Select ${task.key}`}
            />
          </div>
        )}
        {/* Top row: key (type color) | priority 4-bar */}
        <div className={cn('flex items-center justify-between mb-1', selectionMode && 'pl-6')}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className="text-[11px] font-mono font-semibold"
                style={{ color: typeColor }}
              >
                {task.key}
              </span>
            </TooltipTrigger>
            <TooltipContent side="top">{typeLabel} · {task.key}</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <PriorityBars
                  bars={priorityCfg.bars}
                  className={priorityCfg.color}
                />
              </span>
            </TooltipTrigger>
            <TooltipContent side="top">Priority: {priorityCfg.label}</TooltipContent>
          </Tooltip>
        </div>

        {/* Title */}
        <Tooltip>
          <TooltipTrigger asChild>
            <p className="text-[11px] font-medium text-foreground leading-snug mb-1.5 line-clamp-2">
              {task.title}
            </p>
          </TooltipTrigger>
          {task.title.length > 50 && (
            <TooltipContent side="top" className="max-w-[300px]">{task.title}</TooltipContent>
          )}
        </Tooltip>

        {/* Tags — muted with color dots */}
        {task.tags && task.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-1.5">
            {task.tags.slice(0, 3).map((tag) => (
              <Tooltip key={tag.id}>
                <TooltipTrigger asChild>
                  <span className="inline-flex items-center gap-1 rounded-md px-1 py-0.5 text-[10px] font-mono font-medium text-muted-foreground bg-muted/50">
                    <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ backgroundColor: tag.color }} />
                    {tag.name}
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top">Tag: {tag.name}</TooltipContent>
              </Tooltip>
            ))}
            {task.tags.length > 3 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-[10px] font-mono text-muted-foreground">+{task.tags.length - 3}</span>
                </TooltipTrigger>
                <TooltipContent side="top">
                  {task.tags.slice(3).map((t) => t.name).join(', ')}
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        )}

        {/* Bottom row: assignee | checklist, comments, estimation (empty hidden) */}
        <div className="flex items-center justify-between mt-1">
          <div className="flex items-center gap-1.5">
            <AssigneeStack
              people={task.assignees ?? (task.assignee ? [task.assignee] : [])}
              size="xs"
            />
          </div>

          <div className="flex items-center gap-2.5 text-muted-foreground">
            {subtaskCount > 0 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-1 min-w-[28px]">
                    <CheckSquare className="h-2.5 w-2.5 shrink-0" />
                    <span className="text-[10px] tabular-nums min-w-[2ch] text-right">{subtaskCount}</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top">{subtaskCount} subtask{subtaskCount > 1 ? 's' : ''}</TooltipContent>
              </Tooltip>
            )}
            {commentCount > 0 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-1 min-w-[28px]">
                    <MessageSquare className="h-2.5 w-2.5 shrink-0" />
                    <span className="text-[10px] tabular-nums min-w-[2ch] text-right">{commentCount}</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top">{commentCount} comment{commentCount > 1 ? 's' : ''}</TooltipContent>
              </Tooltip>
            )}
            {task.storyPoints != null && task.storyPoints > 0 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center justify-end min-w-[28px]">
                    <span className="text-[10px] font-mono tabular-nums text-right">
                      {task.storyPoints}
                    </span>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top">Estimate: {task.storyPoints} pts</TooltipContent>
              </Tooltip>
            )}
            {task.dueDate && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center justify-end min-w-[32px]">
                    <span className={cn(
                      'text-[10px] tabular-nums text-right',
                      isPastDateOnly(task.dueDate) && 'text-red-400',
                    )}>
                      {formatDate(task.dueDate, 'monthDay')}
                    </span>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top">Due: {formatDate(task.dueDate, 'short')}</TooltipContent>
              </Tooltip>
            )}
            {(task as TaskItem & { totalTimeSeconds?: number }).totalTimeSeconds != null && (task as TaskItem & { totalTimeSeconds?: number }).totalTimeSeconds! > 0 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-1 min-w-[28px]">
                    <Clock className="h-2.5 w-2.5 shrink-0 text-orange-400" />
                    <span className="text-[10px] tabular-nums text-right">
                      {Math.floor((task as TaskItem & { totalTimeSeconds?: number }).totalTimeSeconds! / 3600)}h
                    </span>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top">
                  Time tracked: {Math.floor((task as TaskItem & { totalTimeSeconds?: number }).totalTimeSeconds! / 3600)}h {Math.floor(((task as TaskItem & { totalTimeSeconds?: number }).totalTimeSeconds! % 3600) / 60)}m
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
});
